/* eslint-disable camelcase */
import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { memberNotetakerErasureQueryKey } from './useMemberNotetakerErasure'

import styles from './DeleteNotetakerDataDialog.module.scss'

import { isApiError } from '@/api'
import { startMemberNotetakerErasure } from '@/api/members'
import ConfirmDialog from '@/components/ConfirmDialog'
import FormErrorBanner from '@/components/FormErrorBanner'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { EventName } from '@/utils/eventDefinitions'

interface DeleteNotetakerDataDialogProps {
  userId: string
  email: string
  displayName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ERROR_COPY: Record<string, string> = {
  confirm_email_mismatch: "That email doesn't match this member's email.",
  admin_erasure_requires_billing: 'Deleting Notetaker data requires an active subscription.',
  admin_notetaker_erasure_disabled: "Deleting Notetaker data isn't available right now. Try again later.",
}

const RATE_LIMITED_COPY = 'Too many deletions started. Wait a minute and try again.'

const normalize = (value: string) => value.trim().toLowerCase()

const DeleteNotetakerDataDialog = ({
  userId,
  email,
  displayName,
  open,
  onOpenChange,
}: DeleteNotetakerDataDialogProps) => {
  const [typedEmail, setTypedEmail] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()

  const close = () => {
    onOpenChange(false)
    setTypedEmail('')
    setError(null)
  }

  const mutation = useMutation({
    mutationFn: () => startMemberNotetakerErasure(userId, { confirm_email: typedEmail }),
    onSuccess: () => {
      trackEvent?.(EventName.MemberNotetakerDataErasureStarted, { user_id: userId })
      queryClient.invalidateQueries({ queryKey: memberNotetakerErasureQueryKey(userId) })
      updateToast(`Deleting ${displayName}'s Notetaker data`, 'success')
      close()
    },
    onError: (err: unknown) => {
      if (isApiError(err) && err.status === 429) {
        setError(RATE_LIMITED_COPY)
        return
      }
      const detail = isApiError(err) ? (err.detail ?? err.message) : null
      setError((detail && ERROR_COPY[detail]) ?? detail ?? "Couldn't delete Notetaker data. Try again.")
    },
  })

  return (
    <ConfirmDialog
      isOpen={open}
      title={`Delete ${displayName}'s Notetaker data?`}
      minWidth={512}
      confirmLabel="Delete Notetaker data"
      confirmType="destructive"
      isConfirming={mutation.isPending}
      isConfirmDisabled={normalize(typedEmail) !== normalize(email)}
      onConfirm={() => {
        setError(null)
        mutation.mutate()
      }}
      onCancel={close}
      message={
        <div className={styles.body}>
          <p className={styles.message}>
            This permanently deletes all of {displayName}&apos;s Notetaker meetings, including ones from before they
            joined: transcripts, summaries, recordings, and chats. Their account and other data stay. This can&apos;t be
            undone.
          </p>
          <label className={styles.label}>
            Type {email} to confirm
            <Input
              type="email"
              value={typedEmail}
              onInput={setTypedEmail}
              placeholder={email}
              isDisabled={mutation.isPending}
              ariaLabel="Member email"
            />
          </label>
          {error && <FormErrorBanner message={error} />}
        </div>
      }
    />
  )
}

export default DeleteNotetakerDataDialog
